/**
 * NavDisclosure.js
 *
 * This is the W3 recommendation for how to do a fly-out navigation menu.
 * Sub-menus should open on click, or spacebar if the item is focused.
 * https://www.w3.org/WAI/tutorials/menus/flyout/
 */

class DisclosureNav {
  constructor (domNode) {
    this.rootNode = domNode
    this.timers = []
    this.controlledNodes = []
    this.openIndex = null
    this.useArrowKeys = true // Whether to allo the user to use arrow keyboard keys to navigate through the nav.
    this.desktopQuery = window.matchMedia('(min-width: 768px)')
    this.topLevelNodes = [
      ...this.rootNode.querySelectorAll(
        '.main-link, button[aria-expanded][aria-controls]'
      )
    ]

    // add a class once JS has loaded to avoid flash of unstyled content
    domNode.classList.add('loaded')

    this.topLevelNodes.forEach((node) => {
      // handle button + menu
      if (
        node.tagName.toLowerCase() === 'button' &&
        node.hasAttribute('aria-controls')
      ) {
        const menu = node.parentNode.querySelector('ul')
        if (menu) {
          const li = node.closest('li')
          // save ref controlled menu
          this.controlledNodes.push(menu)

          // collapse menus
          node.setAttribute('aria-expanded', 'false')
          this.toggleMenu(menu, false)

          // attach event listeners
          menu.addEventListener('keydown', this.onMenuKeyDown.bind(this))
          node.addEventListener('click', this.onButtonClick.bind(this))
          node.addEventListener('keydown', this.onButtonKeyDown.bind(this))
          li.addEventListener('mouseover', this.onMouseOver.bind(this))
          li.addEventListener('mouseout', this.onMouseOut.bind(this))
        }
      } else { // handle links
        this.controlledNodes.push(null)
        node.addEventListener('keydown', this.onLinkKeyDown.bind(this))
      }
    })

    this.rootNode.addEventListener('focusout', this.onBlur.bind(this))
  }

  controlFocusByKey (keyboardEvent, nodeList, currentIndex) {
    switch (keyboardEvent.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        keyboardEvent.preventDefault()
        if (currentIndex > -1) {
          const prevIndex = Math.max(0, currentIndex - 1)
          nodeList[prevIndex].focus()
        }
        break
      case 'ArrowDown':
      case 'ArrowRight':
        keyboardEvent.preventDefault()
        if (currentIndex > -1) {
          const nextIndex = Math.min(nodeList.length - 1, currentIndex + 1)
          nodeList[nextIndex].focus()
        }
        break
      case 'Home':
        keyboardEvent.preventDefault()
        nodeList[0].focus()
        break
      case 'End':
        keyboardEvent.preventDefault()
        nodeList[nodeList.length - 1].focus()
        break
      default:
        break
    }
  }

  // public function to close open menu
  close () {
    this.toggleExpand(this.openIndex, false)
  }

  onBlur (event) {
    const menuContainsFocus = this.rootNode.contains(event.relatedTarget)
    if (!menuContainsFocus && this.openIndex !== null) {
      this.toggleExpand(this.openIndex, false)
    }
  }

  onMouseOver (event) {
    if (this.desktopQuery.matches) {
      const button = event.currentTarget.querySelector('button[aria-expanded][aria-controls]')
      const buttonIndex = this.topLevelNodes.indexOf(button)
      clearTimeout(this.timers[buttonIndex])
      this.toggleExpand(buttonIndex, true)
    }
  }

  onMouseOut (event) {
    if (this.desktopQuery.matches) {
      const button = event.currentTarget.querySelector('button[aria-expanded][aria-controls]')
      const buttonIndex = this.topLevelNodes.indexOf(button)
      this.timers[buttonIndex] = setTimeout(this.close.bind(this), 1000)
    }
  }

  onButtonClick (event) {
    const button = event.target
    const buttonIndex = this.topLevelNodes.indexOf(button)
    const buttonExpanded = button.getAttribute('aria-expanded') === 'true'
    this.toggleExpand(buttonIndex, !buttonExpanded)
  }

  onButtonKeyDown (event) {
    const targetButtonIndex = this.topLevelNodes.indexOf(document.activeElement)

    // close on escape
    if (event.key === 'Escape') {
      this.toggleExpand(this.openIndex, false)

    // move focus into the open menu if the current menu is open
    } else if (
      this.useArrowKeys &&
      this.openIndex === targetButtonIndex &&
      event.key === 'ArrowDown'
    ) {
      event.preventDefault()
      this.controlledNodes[this.openIndex].querySelector('a').focus()

    // handle arrow key navigation between top-level buttons, if set
    } else if (this.useArrowKeys) {
      this.controlFocusByKey(event, this.topLevelNodes, targetButtonIndex)
    }
  }

  onLinkKeyDown (event) {
    const targetLinkIndex = this.topLevelNodes.indexOf(document.activeElement)

    // handle arrow key navigation between top-level buttons, if set
    if (this.useArrowKeys) {
      this.controlFocusByKey(event, this.topLevelNodes, targetLinkIndex)
    }
  }

  onMenuKeyDown (event) {
    if (this.openIndex === null) {
      return
    }

    const menuLinks = Array.prototype.slice.call(
      this.controlledNodes[this.openIndex].querySelectorAll('a')
    )
    const currentIndex = menuLinks.indexOf(document.activeElement)

    // close on escape
    if (event.key === 'Escape') {
      this.topLevelNodes[this.openIndex].focus()
      this.toggleExpand(this.openIndex, false)

    // handle arrow key navigation within menu links, if set
    } else if (this.useArrowKeys) {
      this.controlFocusByKey(event, menuLinks, currentIndex)
    }
  }

  toggleExpand (index, expanded) {
    // close open menu, if applicable
    if (this.openIndex !== index) {
      this.toggleExpand(this.openIndex, false)
    }

    // handle menu at called index
    if (this.topLevelNodes[index]) {
      this.openIndex = expanded ? index : null
      this.topLevelNodes[index].setAttribute('aria-expanded', expanded)
      this.toggleMenu(this.controlledNodes[index], expanded)
    }
  }

  toggleMenu (domNode, show) {
    if (domNode) {
      domNode.style.display = show ? 'block' : 'none'
    }
  }
}

/* Initialize Disclosure Menus */
window.addEventListener('load', function () {
  const menus = document.querySelectorAll('.disclosure-nav')
  const disclosureMenus = []
  for (let i = 0; i < menus.length; i++) {
    disclosureMenus[i] = new DisclosureNav(menus[i])
  }
}, false)
